import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import '../../styles/components/SearchableSelect.css';

interface Option {
  value: string;
  label: string;
}

interface SearchableSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /** When provided, typing a query with no exact (case-insensitive) label
   * match shows a "+ Create '<query>'" row that calls this instead of
   * onChange — lets callers offer inline record creation (e.g. a new
   * vendor) without leaving the dropdown. */
  onCreateNew?: (query: string) => void;
  /** Applied to the root .ss-root element — note `className` only reaches
   * the inner .ss-trigger button, so flex/width sizing from a parent row
   * (which needs the actual flex-participating element) has to go here.
   * Prefer `rootClassName` for anything static; this exists for the rare
   * case a value genuinely can't be expressed as a class (a computed
   * per-instance number, not a design constant). */
  style?: React.CSSProperties;
  /** Class(es) applied to the root .ss-root element — the standard way to
   * size/position this component from a parent layout (a form-row's flex
   * sizing, a grid cell, etc.) without reaching for `style`. Added because
   * every caller that needed to style the root had no way to do it with a
   * real CSS class — `className` only reaches `.ss-trigger` — and kept
   * falling back to inline `style` (or a wrapper div) as a workaround. */
  rootClassName?: string;
  /** Plain-language message shown under the control, which is outlined in the
   * error colour — the same look as FormField's `error`. */
  error?: string;
  /** Accessible name for the trigger when no visible label sits next to it (a filter in a header bar). */
  ariaLabel?: string;
}

const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  className = '',
  disabled = false,
  onCreateNew,
  style,
  rootClassName,
  error,
  ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // Portal-rendered dropdown position — computed from the trigger's real
  // screen position so it always escapes any scrolling/overflow:hidden
  // ancestor (previously position:absolute inside .ss-root, which got
  // clipped by parent panels) and flips above the trigger when there's
  // not enough room below (previously always opened downward, running
  // off the bottom of the viewport in some layouts).
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = options.find(o => o.value === value);
  const filtered = query.trim()
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const reposition = () => {
      const rect = triggerRef.current!.getBoundingClientRect();
      const estimatedHeight = 240; // search box + up to ~200px of options
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUpward = spaceBelow < estimatedHeight && rect.top > spaceBelow;
      // minWidth (not width): a narrow trigger like "All ▾" forced the
      // dropdown down to its own width too, truncating longer option
      // labels ("Received from supplier" → "R…") even though there was
      // plenty of room to grow. Let it size to its content instead, capped
      // so it can't run off the right edge of the viewport.
      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        minWidth: rect.width,
        maxWidth: `calc(100vw - ${rect.left + 8}px)`,
        width: 'max-content',
        ...(openUpward
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      });
    };
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleSelect = (opt: Option) => {
    onChange(opt.value);
    setOpen(false);
    setQuery('');
  };

  const trimmedQuery = query.trim();
  const hasExactMatch = options.some(o => o.label.toLowerCase() === trimmedQuery.toLowerCase());
  const showCreateOption = !!onCreateNew && trimmedQuery.length > 0 && !hasExactMatch;

  const handleCreate = () => {
    if (!onCreateNew || !trimmedQuery) return;
    onCreateNew(trimmedQuery);
    setOpen(false);
    setQuery('');
  };

  return (
    <div ref={containerRef} className={`ss-root${rootClassName ? ` ${rootClassName}` : ''}`} style={style}>
      <button
        ref={triggerRef}
        type="button"
        className={`ss-trigger ${className || 'form-input'}${disabled ? ' ss-trigger--disabled' : ''}${error ? ' has-error' : ''}`}
        onClick={handleOpen}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-invalid={error ? true : undefined}
      >
        <span className="ss-trigger-text">{selected ? selected.label : (value || placeholder)}</span>
        <span className="ss-chevron">▾</span>
      </button>

      {open && createPortal(
        <div ref={dropdownRef} className="ss-dropdown" style={dropdownStyle} role="listbox">
          <input
            ref={inputRef}
            className="ss-search"
            type="text"
            placeholder="Search…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') { setOpen(false); setQuery(''); }
              if (e.key === 'Enter') {
                if (filtered.length === 1 && !showCreateOption) handleSelect(filtered[0]);
                else if (showCreateOption) handleCreate();
              }
            }}
          />
          <div className="ss-options">
            {filtered.length === 0 && !showCreateOption ? (
              <div className="ss-empty">No matches</div>
            ) : (
              filtered.map(opt => (
                <div
                  key={opt.value}
                  role="option"
                  aria-selected={opt.value === value}
                  className={`ss-option ${opt.value === value ? 'ss-option--selected' : ''}`}
                  onMouseDown={() => handleSelect(opt)}
                >
                  {opt.label}
                </div>
              ))
            )}
            {showCreateOption && (
              <div
                role="option"
                aria-selected={false}
                className="ss-option ss-option--create"
                onMouseDown={handleCreate}
              >
                + Create "{trimmedQuery}"
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
      {error && <span className="form-error-text">{error}</span>}
    </div>
  );
};

export default SearchableSelect;
